import React, {
  ChangeEventHandler,
  FormEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDocumentTitle } from "usehooks-ts";
import { useAuth, useTranslations } from "../../../contexts";
import { config } from "../../../config";
import {
  Button,
  InlineSpinner,
  Input,
  LoadingErrorState,
} from "../../../components";
import { Save, Check, XIcon } from "lucide-react";
import { client } from "../../../helpers";
import { InferRequestType } from "hono";
import { useNavigate } from "react-router";
import { routerPaths } from "../../routerPaths";
import { toast } from "react-toastify";
import { stringToValidSubdomain } from "../../../../../server/src/helpers/stringToValidSubdomain";
import { AsyncStatus } from "../../../helpers/asyncStatus";

type ClientSitePut = (typeof client.api)["sites"]["$post"];
type ClientSitePutRequest = InferRequestType<ClientSitePut>["json"];

export const SiteNewPage: React.FC = () => {
  const auth = useAuth();
  const header = { authorization: `Bearer ${auth.token}` };
  const i18n = useTranslations();
  const navigate = useNavigate();
  useDocumentTitle(`${config.app.name} - ${i18n["create a new site"]}`);
  const [form, setForm] = useState<ClientSitePutRequest>();
  const [createStatus, setCreateStatus] = useState<AsyncStatus>("idle");
  const [createError, setCreateError] = useState<string | null>(null);
  const [siteExists, setSiteExists] = useState(false);
  const [siteExistsStatus, setSiteExistsStatus] = useState<AsyncStatus>("idle");
  const [siteExistsError, setSiteExistsError] = useState<string | null>(null);
  const latestSlugRef = useRef("");
  const availabilityRequestIdRef = useRef(0);

  const checkSlugAvailability = async (name: string) => {
    const slug = stringToValidSubdomain(name);
    if (!slug) {
      setSiteExists(false);
      setSiteExistsStatus("idle");
      setSiteExistsError(null);
      return;
    }

    const requestId = availabilityRequestIdRef.current + 1;
    availabilityRequestIdRef.current = requestId;
    setSiteExistsStatus("loading");
    setSiteExistsError(null);
    try {
      const results = await (
        await client.api.sites[":slug"].exits.$get({
          header,
          param: { slug },
        })
      ).json();
      if (
        availabilityRequestIdRef.current !== requestId ||
        latestSlugRef.current !== slug
      ) {
        return;
      }
      if (results.success) {
        setSiteExists(results.data.site.exists);
        setSiteExistsStatus("success");
      } else {
        setSiteExists(false);
        setSiteExistsStatus("error");
        setSiteExistsError(i18n["Something went wrong"]);
      }
    } catch (error) {
      console.error(error);
      if (
        availabilityRequestIdRef.current !== requestId ||
        latestSlugRef.current !== slug
      ) {
        return;
      }
      setSiteExists(false);
      setSiteExistsStatus("error");
      setSiteExistsError(i18n["Something went wrong"]);
    }
  };

  useEffect(() => {
    const name = form?.name?.trim() || "";
    if (!name) {
      setSiteExists(false);
      setSiteExistsStatus("idle");
      setSiteExistsError(null);
      return;
    }

    const timeout = window.setTimeout(() => {
      checkSlugAvailability(name);
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [form?.name, auth.token]);

  const createSite = async (payload: ClientSitePutRequest) => {
    if (createStatus === "loading") return;
    setCreateStatus("loading");
    setCreateError(null);
    try {
      const results = await (
        await client.api.sites.$post({
          header,
          json: payload,
        })
      ).json();
      if (results.success) {
        setCreateStatus("success");
        navigate(routerPaths.siteEdit({ siteId: results.data.site.id }));
      } else {
        setCreateStatus("error");
        setCreateError(i18n["Something went wrong"]);
        toast.error(i18n["Something went wrong"]);
      }
    } catch (error) {
      console.error(error);
      setCreateStatus("error");
      setCreateError(i18n["Something went wrong"]);
      toast.error(i18n["Something went wrong"]);
    }
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    if (!form) return;
    if (siteExists || siteExistsStatus === "loading") return;
    await createSite(form);
  };

  const onNameChange: ChangeEventHandler<HTMLInputElement> = async (event) => {
    const name = event.target.value;
    latestSlugRef.current = stringToValidSubdomain(name) || "";
    setForm({ ...form, name });
    if (!name.trim()) {
      setSiteExists(false);
      setSiteExistsStatus("idle");
      setSiteExistsError(null);
      return;
    }
    setSiteExists(false);
    setSiteExistsStatus("loading");
    setSiteExistsError(null);
  };

  const canSubmit =
    Boolean(form?.name) &&
    !siteExists &&
    siteExistsStatus !== "loading" &&
    createStatus !== "loading";

  return (
    <div id="site-new-page" aria-busy={createStatus === "loading"}>
      <form onSubmit={handleSubmit}>
        <h1 className="text-2xl font-bold border-b p-3 mb-6 flex">
          <p className="grow">{i18n["create a new site"]}</p>

          <Button loading={createStatus === "loading"} disabled={!canSubmit}>
            <div className="flex items-center gap-1">
              <Save className="mr-3 h-4 w-4" />
              {i18n.save}
            </div>
          </Button>
        </h1>
        <Input
          onChange={onNameChange}
          placeholder={i18n["name"]}
          disabled={createStatus === "loading"}
        />
        <div className="mt-3 min-h-12 font-bold" aria-live="polite">
          {form?.name && siteExistsStatus === "success" && !siteExists && (
            <p className="text-success flex">
              <Check className="me-2 " />
              <a
                className="underline underline-offset-2"
                href={`https://${stringToValidSubdomain(form.name)}.zuv.fr`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {stringToValidSubdomain(form.name)}.zuv.fr
              </a>{" "}
              {i18n["is available"]}
            </p>
          )}
          {form?.name && siteExistsStatus === "loading" && (
            <InlineSpinner
              showLabel
              className="text-slate-600"
              label={i18n["Checking availability..."]}
            />
          )}
          {form?.name && siteExistsStatus === "success" && siteExists && (
            <p className="text-destructive flex">
              <XIcon className="me-2" />
              <a
                className="underline underline-offset-2"
                href={`https://${stringToValidSubdomain(form.name)}.zuv.fr`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {stringToValidSubdomain(form.name)}.zuv.fr
              </a>{" "}
              {i18n["is already taken"]}
            </p>
          )}
          {siteExistsStatus === "error" && siteExistsError && (
            <LoadingErrorState
              className="w-fit"
              message={siteExistsError}
              retryLabel={i18n["Retry"]}
              onRetry={() => {
                if (form?.name) {
                  checkSlugAvailability(form.name);
                }
              }}
            />
          )}
        </div>
        {createError && (
          <LoadingErrorState
            className="mt-4"
            message={createError}
            retryLabel={i18n["Retry"]}
            onRetry={() => {
              if (form) {
                createSite(form);
              }
            }}
          />
        )}
      </form>
    </div>
  );
};
